/**
 * ITER308-D · Tenant Brand Theming — Admin Settings section.
 *
 * Two controls:
 *   • Primary colour  — colour picker + hex input, live preview.
 *   • Tenant logo     — image upload (≤2MB), replaces the platform logo
 *                        in the sidebar until an outlet-specific logo
 *                        overrides it.
 *
 * "Reset to platform defaults" wipes both — the app reverts to the
 * green baseline + RestroPulse mark instantly.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Palette, Upload, RotateCcw, Trash2 } from 'lucide-react';

const PALETTE = [
  '#143E30', '#0E4C68', '#0F766E', '#4C1D95',
  '#7C2D12', '#8B1E3F', '#1F2937', '#B45309',
];

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export default function AdminBrandingSection() {
  const { getHeaders, API, tenantBranding, refreshBranding, setPreviewPrimaryColor } = useAuth();
  const [color, setColor] = useState(tenantBranding?.primary_color || '#143E30');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const original = tenantBranding?.primary_color || '#143E30';

  useEffect(() => {
    setColor(tenantBranding?.primary_color || '#143E30');
  }, [tenantBranding?.primary_color]);

  // Clear the preview colour when leaving the section so the shell falls
  // back to whatever the admin has actually saved.
  useEffect(() => {
    return () => setPreviewPrimaryColor(null);
  }, [setPreviewPrimaryColor]);

  const previewColor = (hex) => {
    setColor(hex);
    if (HEX_RE.test(hex)) setPreviewPrimaryColor(hex);
  };

  const saveColor = async () => {
    if (!HEX_RE.test(color)) {
      toast.error('Enter a valid #RRGGBB colour');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/branding/tenant`, { primary_color: color },
                       { headers: getHeaders() });
      await refreshBranding();
      setPreviewPrimaryColor(null);
      toast.success('Primary colour saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save colour');
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/admin/branding/tenant/logo`, form,
                        { headers: { ...getHeaders(),
                                       'Content-Type': 'multipart/form-data' } });
      await refreshBranding();
      toast.success('Tenant logo saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeLogo = async () => {
    try {
      await axios.delete(`${API}/admin/branding/tenant/logo`,
                          { headers: getHeaders() });
      await refreshBranding();
      toast.success('Tenant logo removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    }
  };

  const resetAll = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/branding/tenant/reset`, {},
                        { headers: getHeaders() });
      await refreshBranding();
      setPreviewPrimaryColor(null);
      setColor('#143E30');
      toast.success('Brand reset to platform defaults');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setSaving(false); }
  };

  const tenantLogoUrl = tenantBranding?.has_logo && tenantBranding?.tenant_id
    ? `${API}/branding/tenant/${tenantBranding.tenant_id}/logo?v=${tenantBranding.logo_version || 0}`
    : null;

  return (
    <Card className="border-[#E2E8DF] shadow-sm mt-6" data-testid="branding-card">
      <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-[#F9F9F8]">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
            <Palette className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
            Brand Theme
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={resetAll}
                  disabled={saving}
                  className="h-7 text-[11px] text-[#5C6B64] hover:text-[#1A2421]"
                  data-testid="brand-reset-btn">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset to platform default
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 py-4 space-y-6">
        {/* Primary colour */}
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">
            Primary Colour
          </Label>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => previewColor(e.target.value)}
                className="w-11 h-11 rounded-md border border-[#E2E8DF] cursor-pointer p-0"
                data-testid="brand-color-picker"
              />
              <Input
                value={color}
                onChange={(e) => previewColor(e.target.value)}
                placeholder="#143E30"
                className="w-32 h-11 font-mono text-sm border-[#E2E8DF]"
                data-testid="brand-color-hex"
              />
            </div>
            <div className="flex items-center gap-1.5">
              {PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => previewColor(c)}
                         className={`w-7 h-7 rounded-full border transition-transform ${
                           color.toLowerCase() === c.toLowerCase()
                             ? 'ring-2 ring-offset-1 ring-[color:var(--color-primary)] scale-110'
                             : 'border-[#E2E8DF] hover:scale-105'
                         }`}
                         style={{ background: c }}
                         title={c}
                         data-testid={`brand-swatch-${c.slice(1)}`} />
              ))}
            </div>
            <Button
              onClick={saveColor}
              disabled={saving || color === original}
              className="h-10 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white ml-auto"
              data-testid="brand-color-save"
            >
              Save colour
            </Button>
          </div>
          <p className="text-[11px] text-[#5C6B64] mt-2">
            Applied instantly to buttons, tabs, sidebar highlights, chart accents, and Maintenance section bands. Guest loyalty surfaces intentionally keep the platform navy + gold palette.
          </p>
        </div>

        {/* Logo */}
        <div className="border-t border-[#E2E8DF] pt-4">
          <Label className="text-[11px] uppercase tracking-wider text-[#5C6B64] font-semibold">
            Tenant Logo
          </Label>
          <div className="mt-2 flex items-center gap-4 flex-wrap">
            <div className="w-32 h-16 border border-[#E2E8DF] rounded-md bg-[#F9F9F8] flex items-center justify-center overflow-hidden">
              {tenantLogoUrl ? (
                <img src={tenantLogoUrl} alt="Tenant logo"
                      className="max-w-full max-h-full object-contain"
                      data-testid="brand-logo-preview" />
              ) : (
                <span className="text-[10px] text-[#5C6B64] italic">No logo</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*"
                     onChange={(e) => uploadLogo(e.target.files?.[0])}
                     className="hidden"
                     data-testid="brand-logo-file" />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="h-9 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                data-testid="brand-logo-upload">
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                {uploading ? 'Uploading…' : tenantLogoUrl ? 'Replace logo' : 'Upload logo'}
              </Button>
              {tenantLogoUrl && (
                <Button variant="ghost" size="sm" onClick={removeLogo}
                        className="h-8 text-[11px] text-[#C45A41] hover:text-[#C45A41] hover:bg-[#C45A41]/10 justify-start px-2"
                        data-testid="brand-logo-remove">
                  <Trash2 className="w-3 h-3 mr-1" /> Remove
                </Button>
              )}
            </div>
            <div className="text-[11px] text-[#5C6B64] max-w-xs leading-relaxed">
              PNG · JPG · SVG · under 2 MB. Shown in the sidebar for admin and area-manager users. Manager view falls back to their outlet logo if set.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
